// Ephemeral UI state: which screen/overlay is showing, viewport mode, system
// dark preference, and the last error banner.

import type { StateCreator } from 'zustand';
import type { Route } from '@/lib/route';

/**
 * `isw` is the standard-work library, reached from the More tab.
 *
 * A screen rather than an overlay because it replaces the whole frame, tab bar
 * included — the operator is reading, and a tab bar under a work instruction is
 * an invitation to lose their place. The card viewer above it IS an overlay,
 * which is why it has no screen of its own.
 *
 * `raise` is the andon raise flow, which is a sheet over the tab it was
 * opened from, with a URL (/raise) because the Andon app ran it as a place
 * of its own and a push can send somebody straight to it. Only opened while
 * `capabilities.andon.preview` is on; without it the dial's Andon entry
 * hands over to the Andon app instead.
 */
export type Screen = 'login' | 'home' | 'job' | 'complete' | 'ach' | 'isw' | 'raise';

/**
 * The persistent bottom bar: Home · Pods · Quality · Andon · More (frame
 * v2.12) — five plain places, no centre control. `alerts` is the Quality tab:
 * it keeps the key and the /alerts path it had when it was Alerts, and its
 * andon group moved to the `andon` tab. Scan and the raise kinds are on the
 * floating + above the bar (`PlusFab`), and on a tablet in the header and at
 * the foot of the rail. See `lib/tabs` for the model.
 */
export type Tab = 'home' | 'pods' | 'alerts' | 'andon' | 'more';

/**
 * Which app a leader is looking at.
 *
 * The leader home and the operator view are the same app, and the path between
 * them is drilling into a work centre. A leader starts on the floor; picking a
 * station puts them in the operator view for it, and the way back is explicit
 * — a leader who has walked to a station should not lose it by tapping Home.
 */
export type LeaderView = 'floor' | 'station';

export type Overlay =
  | 'settings'
  | 'station'
  | 'report'
  | 'block'
  | 'form'
  | 'raised'
  | 'docs'
  | 'search'
  | 'camera'
  | 'upstream'
  | 'quick'
  | 'issues'
  | 'win'
  | 'pause'
  | 'boardComplete'
  /** The three-type picker: andon, quality, safety (frame 1f). */
  | 'raise'
  /** Handing over to the Andon app, which owns raising and acknowledging. */
  | 'andon'
  /** One quality alert, with its stage, category, photos and chatter. */
  | 'quality'
  /** One andon, read in full before deciding whether to walk over. */
  | 'andonDetail'
  /** Today's shifts from attendance: who is on now, and who has finished. */
  | 'crew'
  /** Pushed andon notifications, and whether this device can receive them. */
  | 'notifications';

/**
 * Where the phone layout becomes the tablet one.
 *
 * One shell, two arrangements: under this width the tabs sit along the bottom
 * and the content is one column; at or above it the tabs move to a rail on the
 * left, the scope chips and the score move into the header, and a second rail
 * on the right carries what a phone tucks into sheets. It is a grid breakpoint
 * and nothing more — there is no separate board surface any more. The old
 * 1000px board never fitted the 8–10 inch handheld tablets the floor actually
 * carries, which got the phone layout stretched across a landscape screen.
 */
export const TABLET_MIN_WIDTH = 700;

/** The leader's home is three views of the same floor. */
export type FloorView = 'overview' | 'stations' | 'buffers';

export interface UiSlice {
  screen: Screen;
  /**
   * The work order the job screen is showing.
   *
   * Which pod that is stays the queue's business — this is only the app saying
   * out loud which one it opened, so the URL can name it and a refresh can find
   * it again. See `lib/route`.
   */
  openWoId: number | null;
  /**
   * The ISW whose card is open over the library, as its register id.
   *
   * In the store rather than in the screen so the URL can name it: an operator
   * reading a card at a cell who loses the tablet to a reload comes back to
   * the card, not to the list. Which FORMAT and language they were reading is
   * deliberately not carried — those reset to the mobile card in the app's own
   * language, which is what the row opens by default.
   */
  openIswId: string | null;
  tab: Tab;
  /**
   * The flowline the Pods tab should open on, set by whatever sent the
   * operator there — the line's badge on the home screen. Consumed by the
   * screen on arrival and never persisted: a landing hint, not a place.
   */
  podsLane: number | null;
  /** Only meaningful in a leader session; operators are always 'station'. */
  leaderView: LeaderView;
  /** Which of the leader's three home views is showing. Not a place: no URL. */
  floorView: FloorView;
  overlay: Overlay | null;
  /** The record a detail sheet is open on, when the overlay is one of those. */
  openAlertId: number | null;
  openAndonId: number | null;
  /**
   * A short confirmation after a write — "Now at FL - Plumbing", "QA32731 →
   * Closed". It says what just happened and goes away on its own; the state
   * it reports is already on screen, so it is never the only record of it.
   */
  toast: { msg: string; ts: number } | null;
  /** Document code being read full-screen — a layer above overlays. */
  docView: string | null;
  vw: number;
  sysDark: boolean;
  scanning: boolean;
  lastError: { msg: string; ts: number } | null;
  /** A newer bundle is downloaded and waiting for somebody to say yes. */
  updateReady: boolean;
  /**
   * Somebody has seen the update banner and said not now.
   *
   * Not persisted, and deliberately: a device that dismissed once must not be
   * a device that is never asked again. It comes back on the next load, and
   * `setUpdateReady` clears it so a NEWER build than the one waved away asks
   * for itself. Dismissing hides the interruption, never the choice.
   */
  updateDismissed: boolean;

  setScreen: (s: Screen) => void;
  /** Open the andon raise flow as a screen of its own. Preview only. */
  openRaise: () => void;
  /** Open a pod's job screen — the one navigation that names a record. */
  openJob: (woId: number) => void;
  /** Open an ISW's card over the library; null closes it back to the list. */
  openIsw: (iswId: string | null) => void;
  /** Go where a URL says, applied in one write so the app never lands half-way. */
  goToRoute: (r: Route) => void;
  setTab: (t: Tab) => void;
  /** Go to Pods, opened on one flowline's lane (null for wherever it lands). */
  openPods: (flowlineId: number | null) => void;
  clearPodsLane: () => void;
  setLeaderView: (v: LeaderView) => void;
  setFloorView: (v: FloorView) => void;
  openOverlay: (o: Overlay) => void;
  /** Open the quality sheet on one alert. */
  openQualityAlert: (alertId: number) => void;
  /** Open the andon sheet on one alert. */
  openAndon: (andonId: number) => void;
  closeOverlay: () => void;
  showToast: (msg: string) => void;
  clearToast: () => void;
  openDoc: (code: string) => void;
  closeDoc: () => void;
  setViewport: (vw: number) => void;
  setSysDark: (v: boolean) => void;
  setScanning: (v: boolean) => void;
  showError: (msg: string) => void;
  clearError: () => void;
  setUpdateReady: (v: boolean) => void;
  dismissUpdate: () => void;
}

export const createUiSlice: StateCreator<UiSlice, [], [], UiSlice> = (set) => ({
  screen: 'login',
  openWoId: null,
  openIswId: null,
  tab: 'home',
  podsLane: null,
  leaderView: 'floor',
  floorView: 'overview',
  overlay: null,
  openAlertId: null,
  openAndonId: null,
  toast: null,
  docView: null,
  vw: typeof window === 'undefined' ? 1200 : window.innerWidth,
  sysDark: false,
  scanning: false,
  lastError: null,
  updateReady: false,
  updateDismissed: false,

  // Leaving the job screen forgets which pod it was on, so the URL cannot go on
  // naming a pod nobody is looking at.
  setScreen: (screen) =>
    set({
      screen,
      overlay: null,
      ...(screen === 'job' ? {} : { openWoId: null }),
      // Same reasoning as openWoId: leaving the library must not leave the URL
      // naming a card nobody is looking at.
      ...(screen === 'isw' ? {} : { openIswId: null }),
    }),
  // The pod in hand is not forgotten here: the raise reads it from the andon
  // slice's draft, which the caller fills before opening. The job URL goes,
  // because /raise is where the operator now is.
  openRaise: () => set({ screen: 'raise', openWoId: null, overlay: null }),
  openJob: (openWoId) => set({ screen: 'job', openWoId, overlay: null }),
  openIsw: (openIswId) => set({ screen: 'isw', openIswId }),
  goToRoute: (r) =>
    set({
      leaderView: r.leaderView,
      tab: r.tab,
      screen: r.screen,
      openWoId: r.screen === 'job' ? r.woId : null,
      openIswId: r.screen === 'isw' ? r.iswId : null,
      // An alert URL opens its sheet over the Andon tab: the one overlay a
      // URL names, because a tapped notification has to land ON the andon
      // and not beside it. See `lib/route`.
      overlay: r.andonId != null ? 'andonDetail' : null,
      openAndonId: r.andonId,
      docView: null,
    }),
  // Changing tab closes whatever was over it. A sheet left open across a tab
  // change belongs to the screen behind it, not to the one now showing.
  setTab: (tab) => set({ tab, screen: 'home', openWoId: null, overlay: null }),
  openPods: (podsLane) => set({ tab: 'pods', podsLane, screen: 'home', openWoId: null, overlay: null }),
  clearPodsLane: () => set({ podsLane: null }),
  setLeaderView: (leaderView) =>
    set({ leaderView, screen: 'home', openWoId: null, tab: 'home', overlay: null }),
  setFloorView: (floorView) => set({ floorView }),
  openOverlay: (overlay) => set({ overlay }),
  openQualityAlert: (openAlertId) => set({ overlay: 'quality', openAlertId }),
  openAndon: (openAndonId) => set({ overlay: 'andonDetail', openAndonId }),
  closeOverlay: () => set({ overlay: null, openAlertId: null, openAndonId: null }),
  showToast: (msg) => set({ toast: { msg, ts: Date.now() } }),
  clearToast: () => set({ toast: null }),
  openDoc: (docView) => set({ docView }),
  closeDoc: () => set({ docView: null }),
  setViewport: (vw) => set({ vw }),
  setSysDark: (sysDark) => set({ sysDark }),
  setScanning: (scanning) => set({ scanning }),
  showError: (msg) => set({ lastError: { msg, ts: Date.now() } }),
  clearError: () => set({ lastError: null }),
  setUpdateReady: (updateReady) => set({ updateReady, updateDismissed: false }),
  dismissUpdate: () => set({ updateDismissed: true }),
});

export const isTablet = (vw: number): boolean => vw >= TABLET_MIN_WIDTH;
